/**
 * @file Pieces every call mounts inside its <LiveKitRoom> to keep people in
 * the call and heard (#2223): mute-state sync, device recovery, replacing a
 * connection that silently stopped carrying media, and the "Reconnecting…" pill.
 *
 * Shared by the signed-in call (CallSessionRoom) and the guest-link call
 * (internal/MeetingRoom) so a guest recovers exactly like a signed-in user.
 * Nothing here reads a session store: each caller says what to do with the
 * values through props.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRoomContext } from "@livekit/components-react";
import { ConnectionState, RoomEvent, Track } from "livekit-client";
import { useLocalMediaRecovery } from "../hooks/useLocalMediaRecovery";
import { useMediaStallRecovery } from "../hooks/useMediaStallRecovery";

/**
 * Reports what the user has on right now (#2223 AC10, AC15).
 *
 * <LiveKitRoom> publishes mic and camera per its `audio`/`video` props on
 * every connect -- including a rejoin after a dropped connection -- so with
 * the join-time values a rejoin silently re-muted someone who had unmuted
 * (the others stop hearing them) or unmuted someone who had muted. Only read
 * while connected: during a reconnect LiveKit unpublishes and republishes
 * tracks itself, which must not count as the user turning them off.
 *
 * An unpublish is never a user choice here (turning mic or camera off is a
 * mute), so it is not listened to at all. A disconnect of a room that still
 * says "connected" -- what the stall watchdog does -- unpublishes both tracks
 * before the state flips, and counting that brought the person back silent.
 */
export function LocalMediaStateSync({
  onChange,
}: {
  onChange: (audioEnabled: boolean, videoEnabled: boolean) => void;
}) {
  const room = useRoomContext();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const sync = () => {
      if (room.state !== ConnectionState.Connected) return;
      const lp = room.localParticipant;
      const on = (source: Track.Source) => {
        const pub = lp.getTrackPublication(source);
        return !!pub && !pub.isMuted;
      };
      onChangeRef.current(on(Track.Source.Microphone), on(Track.Source.Camera));
    };
    const events = [
      RoomEvent.LocalTrackPublished,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
    ] as const;
    for (const event of events) room.on(event, sync);
    return () => {
      for (const event of events) room.off(event, sync);
    };
  }, [room]);
  return null;
}

/** Restarts mic/camera the device interrupted (#2223 AC8–AC11). */
export function LocalMediaRecovery() {
  useLocalMediaRecovery(useRoomContext());
  return null;
}

/**
 * Brings someone back when their connection looks alive but carries nothing
 * (#2223 AC13) -- e.g. a Wi-Fi→4G switch the server never accepted.
 */
export function MediaStallRecovery() {
  useMediaStallRecovery(useRoomContext());
  return null;
}

/** Fixed dark pill shown while a call is bringing someone back (#1178). */
export function ReconnectingPill({ onLeave }: { onLeave: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="call-reconnecting-indicator"
      role="status"
      aria-live="polite"
      className="fixed left-1/2 top-4 -translate-x-1/2 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-lg"
      style={{
        // Fixed dark pill regardless of site theme, so the text and border
        // stay fixed-light too: the theme-aware text tokens flip to
        // near-black in light mode and would be unreadable on it.
        zIndex: 9999,
        background: "#1f2430",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.15)",
      }}
    >
      <span
        className="inline-block h-4 w-4 animate-spin rounded-full border-2"
        style={{
          borderColor: "rgba(255,255,255,0.3)",
          borderTopColor: "var(--accent)",
        }}
      />
      {t("call.connection.reconnectingTitle")}
      <button
        type="button"
        data-testid="call-reconnect-leave"
        onClick={onLeave}
        className="ms-1 rounded-full px-3 py-1 text-xs font-semibold"
        style={{ background: "rgba(255,255,255,0.12)", color: "#fff" }}
      >
        {t("call.connection.leave")}
      </button>
    </div>
  );
}
